import type { ReactNode } from 'react'
import { ChevronRight, Users } from 'lucide-react'
import { Badge, RoleBadge, StatusBadge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/Surface'
import { Button } from '@/components/ui/Button'
import { dealerRole, initialsOf } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { Dealer } from '@/types'

const TH = 'label-caps px-4 py-2.5 text-left text-subtle'
const TD = 'px-4 py-2.5 align-middle'

function DaysLeft({ dealer }: { dealer: Dealer }): ReactNode {
  if (dealer.is_admin) return <span className="text-subtle">—</span>
  const days = dealer.days_remaining || 0
  if (days === 0) return <span className="text-subtle">—</span>
  const tone = days <= 3 ? 'text-negative' : days <= 7 ? 'text-caution' : 'text-fg'
  return (
    <span className={cn('font-medium tabular-nums', tone)}>
      {days}
      <span className="ml-0.5 text-[10.5px] font-normal text-subtle">D</span>
    </span>
  )
}

export interface DealerTableProps {
  dealers: Dealer[]
  totalCount: number
  onSelect: (userId: string) => void
  onClearFilters: () => void
}

export function DealerTable({
  dealers,
  totalCount,
  onSelect,
  onClearFilters
}: DealerTableProps): ReactNode {
  if (dealers.length === 0) {
    return totalCount === 0 ? (
      <EmptyState
        icon={Users}
        title="No dealers registered"
        description="Accounts appear here once customers register and verify their email in the desktop app."
      />
    ) : (
      <EmptyState
        icon={Users}
        title="No dealers match these filters"
        description="Try a different search term, or clear the filters to see all accounts."
        action={
          <Button variant="secondary" size="sm" onClick={onClearFilters}>
            Clear filters
          </Button>
        }
      />
    )
  }

  return (
    <div className="scroll-slim overflow-x-auto">
      <table className="w-full min-w-[860px] border-collapse">
        <thead className="border-b border-line bg-raised">
          <tr>
            <th className={TH}>Dealer</th>
            <th className={TH}>ID</th>
            <th className={TH}>Role</th>
            <th className={TH}>Status</th>
            <th className={TH + ' text-right'}>Days left</th>
            <th className={TH + ' text-right'}>Seats</th>
            <th className={TH}>Account</th>
            <th className={TH} />
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {dealers.map((d) => (
            <tr
              key={d.user_id}
              onClick={() => onSelect(d.user_id)}
              className="group cursor-pointer transition-colors hover:bg-raised"
            >
              <td className={TD}>
                <div className="flex items-center gap-2.5">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded border border-line bg-raised text-[10.5px] font-bold text-muted group-hover:border-accent/40 group-hover:text-accent">
                    {initialsOf(d.name)}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-[12.5px] font-medium text-fg">{d.name}</p>
                    <p className="truncate text-[11px] text-muted">{d.email}</p>
                  </div>
                </div>
              </td>
              <td className={TD}>
                <span className="font-mono text-[11.5px] text-muted">{d.user_id}</span>
              </td>
              <td className={TD}>
                <RoleBadge role={dealerRole(d)} />
              </td>
              <td className={TD}>
                <StatusBadge status={d.status} />
              </td>
              <td className={TD + ' text-right'}>
                <DaysLeft dealer={d} />
              </td>
              <td className={TD + ' text-right'}>
                <span className="text-[12px] tabular-nums text-muted">{d.allowed_clients}</span>
              </td>
              <td className={TD}>
                {d.is_active ? (
                  <Badge tone="green">Active</Badge>
                ) : (
                  <Badge tone="red">Disabled</Badge>
                )}
              </td>
              <td className={TD + ' text-right'}>
                <ChevronRight
                  size={15}
                  className="ml-auto text-subtle group-hover:text-accent"
                  aria-hidden
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
